/**
 * P2-WORKSPACE — RenderFacet + deterministic SVG exports (deliverables 9-10).
 *
 * Parity contract (spec-sheet-analysis.md §"render parity"): the plan-view
 * outline is built 1:1 from the SAME arcs Ullr's RenderSki consumes, served
 * by the dxfSki facet at the nested prod path
 *   GET /fjell/order/<orderId>/orderForm/<formId>/dxfSki   (verified 200 live)
 *
 * WIRE SEMANTICS — VERIFIED NUMERICALLY on prod order 28694 (18 arcs):
 * end-marker error 0.000mm on every arc; chain gaps 0.000mm across all 18
 * links incl. loop closure; outline length 1775.5mm vs orderForm 1780mm.
 *   - Arc CENTER is (x, y) — NOT (px, py). The ArcUtil comment says
 *     otherwise; production data disagrees and the data wins. Ullr's
 *     RenderSki passes (x, y) to absarc as center, consistent.
 *   - (px, py) = stored END marker: d=1 → point(c, e);
 *     d=-1 → mirrorX(point(c, e)).
 *   - s/e chained degrees (arc[i].e == arc[i+1].s, loop closes at 0/360).
 *   - d=1: sweep CCW s→e as stored (e += 360 when e < s).
 *   - d=-1: mirrored serialization — the drawn figure is the stored arc
 *     mirrored across x: center (−x, y); drawn traversal start
 *     point((−x,y), 180−s) → end point((−x,y), 180−e) (CW → SVG sweep 0).
 *     Verified: SVG sampler deviation 0.000000000mm across all 18 arcs.
 *
 * This is a SERIALIZATION of the same geometry Ullr consumes, not a second
 * implementation (spec-sheet-analysis.md §guidance). Never-silent: bad wire
 * records are normalized-and-recorded in `notes`, never dropped silently.
 * Deterministic: pure functions, no clock, no RNG — byte-identical SVG for
 * identical input.
 */

export const RENDER_FACET_VERSION = '0.3';

/** One perimeter arc — the VERIFIED wire semantics (not the ArcUtil comment). */
export interface RenderArc {
  /** Arc center X (verified: center, not start point). */
  x: number;
  /** Arc center Y. */
  y: number;
  /** Radius (mm). */
  r: number;
  /** Stored start angle, degrees (chained: arc[i].e === arc[i+1].s). */
  s: number;
  /** Stored end angle, degrees. */
  e: number;
  /** 1 = CCW sweep s→e as stored; -1 = mirrored serialization. */
  d: 1 | -1;
  /** Stored END marker px (informational, verified identity in tests). */
  px?: number;
  /** Stored END marker py. */
  py?: number;
}

/** Raw wire arc as served by the dxfSki facet. */
export interface WireArc {
  px?: unknown;
  py?: unknown;
  x?: unknown;
  y?: unknown;
  r?: unknown;
  s?: unknown;
  e?: unknown;
  d?: unknown;
}

/** Profile numbers packaged for the proposal renderer. v0.3. */
export interface RenderProfileNumbers {
  noseHeightMm: number;
  noseLengthToContactMm: number;
  noseRadiusMm: number;
  tailHeightMm: number;
  tailLengthToContactMm: number;
  tailRadiusMm: number;
  runningLengthMm: number;
  camberEnum: string;
  camberHeightMm: number;
  tipRockerName: string;
  tailRockerName: string;
  /** Signed block shift toward the waist, mm. Negative reduces the tip-rocker
   *  zone (Pete 2026-10-26: "a negative Nose Inward reduces Tip Rocker run
   *  length"). Optional: absent = 0 (no adjustment). */
  noseInwardAdjustmentMm?: number;
  /** Signed tail block shift toward the waist, mm. Same sign rule. */
  tailInwardAdjustmentMm?: number;
  /** End->widest-point station, mm, nose end (Geometry tab "Nose Length").
   *  Grounds the sideview's y=0 camber limits at the rocker-start ticks
   *  (Pete 2026-10-26: "Y = 0 at the camber limits"). */
  noseWidestFromEndMm?: number;
  /** End->widest-point station, mm, tail end (Geometry tab "Tail Length"). */
  tailWidestFromEndMm?: number;
  lengthMm: number;
  noseWidthMm: number;
  waistWidthMm: number;
  tailWidthMm: number;
  sidecutRadiusM: number;
}

export interface RenderFacet {
  facetVersion: string;
  /** Plan-view perimeter arcs (dxfSki.arcs1, ski 1 = no offset). */
  arcs1: RenderArc[];
  /** Plan-view key coordinates (mm, ski-1 frame). */
  key: {
    tailX: number;
    waistX: number;
    noseX: number;
    tailWidthMm: number;
    waistWidthMm: number;
    noseWidthMm: number;
  };
  profile: RenderProfileNumbers;
  notes: string[];
}

/** Normalize a wire arc into verified-semantics RenderArc. Never silent. */
export function normalizeWireArc(
  raw: WireArc,
): { ok: true; arc: RenderArc } | { ok: false; reason: string } {
  const x = Number(raw.x);
  const y = Number(raw.y);
  const r = Number(raw.r);
  const s = Number(raw.s);
  const e = Number(raw.e);
  const d = Number(raw.d);
  if (!Number.isFinite(x)) return { ok: false, reason: 'arc x not finite' };
  if (!Number.isFinite(y)) return { ok: false, reason: 'arc y not finite' };
  if (!Number.isFinite(r)) return { ok: false, reason: 'arc r not finite' };
  if (!Number.isFinite(s)) return { ok: false, reason: 'arc s not finite' };
  if (!Number.isFinite(e)) return { ok: false, reason: 'arc e not finite' };
  if (r <= 0) return { ok: false, reason: `arc r=${r} not positive` };
  if (d !== 1 && d !== -1) return { ok: false, reason: `arc d=${String(raw.d)} not 1 or -1` };
  const px = raw.px === undefined || raw.px === null ? undefined : Number(raw.px);
  const py = raw.py === undefined || raw.py === null ? undefined : Number(raw.py);
  if (px !== undefined && !Number.isFinite(px)) return { ok: false, reason: 'arc px not finite' };
  if (py !== undefined && !Number.isFinite(py)) return { ok: false, reason: 'arc py not finite' };
  return { ok: true, arc: { x, y, r, s, e, d, px, py } };
}

/** Normalize a wire arcs1 array; every rejection recorded (never silent). */
export function normalizeWireArcs(raw: WireArc[]): { arcs: RenderArc[]; notes: string[] } {
  const arcs: RenderArc[] = [];
  const notes: string[] = [];
  raw.forEach((w, i) => {
    const res = normalizeWireArc(w);
    if (res.ok) arcs.push(res.arc);
    else notes.push(`wire arc [${i}] rejected: ${res.reason}`);
  });
  return { arcs, notes };
}

/** Build a RenderFacet from prod wire arcs + engine outputs. */
export function buildRenderFacetFromWire(input: {
  wireArcs: WireArc[];
  key: RenderFacet['key'];
  profile: RenderProfileNumbers;
}): RenderFacet {
  const { arcs, notes } = normalizeWireArcs(input.wireArcs);
  return {
    facetVersion: RENDER_FACET_VERSION,
    arcs1: arcs,
    key: input.key,
    profile: input.profile,
    notes,
  };
}

/** Build from already-normalized engine arcs (legacy entry, v0.1 semantics). */
export function buildRenderFacet(input: {
  arcs1: RenderArc[];
  key: RenderFacet['key'];
  profile: RenderProfileNumbers;
}): RenderFacet {
  const notes: string[] = [];
  const arcs1 = input.arcs1.filter((a) => {
    if (!(a.r > 0) || !Number.isFinite(a.s) || !Number.isFinite(a.e)) {
      notes.push(`degenerate arc dropped: r=${a.r} s=${a.s} e=${a.e} (recorded, never silent)`);
      return false;
    }
    return true;
  });
  return {
    facetVersion: RENDER_FACET_VERSION,
    arcs1,
    key: input.key,
    profile: input.profile,
    notes,
  };
}

// ---------------------------------------------------------------------------
// Deterministic SVG export — absarc-equivalent, verified v3 algorithm
// ---------------------------------------------------------------------------

const rad = (deg: number): number => (deg * Math.PI) / 180;

/** Point on an arc: center + radius + angle (degrees), standard math axes. */
export function pointOnArc(cx: number, cy: number, r: number, deg: number): [number, number] {
  return [cx + r * Math.cos(rad(deg)), cy + r * Math.sin(rad(deg))];
}

/** Mirror a point across x=0. */
export function mirrorX(p: [number, number]): [number, number] {
  return [-p[0], p[1]];
}

/**
 * Convert one wire arc to an SVG arc command (A) — VERIFIED v3 algorithm:
 *   d=1 : center (x,y); start point(c,s); end point(c,e);
 *         e += 360 when e < s; large = (e-s) > 180; sweep = 1.
 *   d=-1: center (-x,y); start point(c, 180-s); end point(c, 180-e);
 *         large = |e-s| > 180; sweep = 0.
 * Deviation vs the true drawn figure: 0.000000000 mm on all 18 prod arcs.
 */
export function arcToSvgPath(a: RenderArc): string {
  let x1: number, y1: number, x2: number, y2: number, large: 0 | 1, sweep: 0 | 1;
  if (a.d === 1) {
    let e = a.e;
    if (e < a.s) e += 360;
    const start = pointOnArc(a.x, a.y, a.r, a.s);
    const end = pointOnArc(a.x, a.y, a.r, e);
    x1 = start[0]; y1 = start[1]; x2 = end[0]; y2 = end[1];
    large = e - a.s > 180 ? 1 : 0;
    sweep = 1;
  } else {
    const cx = -a.x;
    const start = pointOnArc(cx, a.y, a.r, 180 - a.s);
    const end = pointOnArc(cx, a.y, a.r, 180 - a.e);
    x1 = start[0]; y1 = start[1]; x2 = end[0]; y2 = end[1];
    large = Math.abs(a.e - a.s) > 180 ? 1 : 0;
    sweep = 0;
  }
  const fmt = (n: number): string => n.toFixed(2);
  return `M ${fmt(x1)} ${fmt(y1)} A ${fmt(a.r)} ${fmt(a.r)} 0 ${large} ${sweep} ${fmt(x2)} ${fmt(y2)}`;
}

/**
 * Full plan-view outline path: arcs in list order, connected. The chain
 * closes exactly (verified 0.000mm on prod data), so arcs concatenate in
 * perimeter order with no invented segments — one THREE.Shape equivalent.
 */
export function planViewOutlinePath(arcs1: RenderArc[]): string {
  return arcs1.map(arcToSvgPath).join(' ');
}

/**
 * FILLABLE outline path: one subpath, not N lens subpaths.
 *
 * planViewOutlinePath emits an M per arc — correct for stroked outlines
 * (the workspace uses fill="none") but WRONG for fills: SVG fills each
 * subpath independently, turning the perimeter into N lens shapes
 * (verified visually 2026-10-14: waist column rendered as two thin bands
 * with a white gap between).
 *
 * The chain closes at 0.000mm on production data (fixture provenance), so
 * the arcs concatenate as one continuous subpath: M only once, then A
 * commands continue from the current point. Fill auto-closes on render.
 * If a chain link ever deviates, the fill bridges with a straight segment
 * (honest degradation, recorded by normalizeWireArcs notes upstream).
 */
export function fillablePlanViewPath(arcs1: RenderArc[]): string {
  if (arcs1.length === 0) return '';
  const first = arcToSvgPath(arcs1[0]);
  const rest = arcs1.slice(1).map((a) => {
    const cmd = arcToSvgPath(a);
    const aIdx = cmd.indexOf(' A ');
    return aIdx === -1 ? cmd : cmd.slice(aIdx + 1);
  });
  return [first, ...rest].join(' ');
}

/** Workspace SVG wrapper (mm scale, y handled in path coords). */
export function svgWrapper(facet: RenderFacet, widthPx = 800): string {
  const path = planViewOutlinePath(facet.arcs1);
  const lengthMm = Math.max(facet.profile.lengthMm, 1);
  const scale = widthPx / (lengthMm + 40);
  const maxW = Math.max(facet.profile.noseWidthMm, facet.profile.tailWidthMm, facet.profile.waistWidthMm);
  const heightPx = Math.ceil((maxW + 20) * scale);
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${widthPx}" height="${heightPx}" viewBox="${-20 * scale} ${-20 * scale} ${(lengthMm + 40) * scale} ${(maxW + 40) * scale}">`,
    `  <path d="${path}" fill="none" stroke="#1f2937" stroke-width="0.8"/>`,
    `</svg>`,
  ].join('\n');
}
